import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ResponsiveTable from './ResponsiveTable';
import ClientLink from './ClientLink';
import Pager from './UI/Pager';
import StatusBadge from './UI/StatusBadge';
import FullScreenPanel from './UI/FullScreenPanel';
import DisplayField, { FieldGroup } from './UI/DisplayField';
import Button from './UI/Button';
import { RICH_TEXT_CLASS } from './UI/richText';
import { formatDateTimeDMY } from '../utils/dateUtils';
import { formatBytes } from '../utils/formatBytes';
import { LINK_TEXT } from '../utils/clickable';
import { Paperclip, Download, RotateCw } from 'lucide-react';

// Who a message is sent to (Messages > Send), and how a sent one says it.
export const MESSAGE_SCENARIOS = [
  { value: 'all_clients', label: 'All Clients' },
  { value: 'by_loan_status', label: 'By Loan Status' },
  { value: 'individual_client', label: 'Individual Client' },
  { value: 'clients_without_loans', label: 'Clients Without Loans' },
  { value: 'clients_with_active_loans', label: 'Clients With Active Loans' },
  { value: 'custom_client_list', label: 'Custom Client List' },
];

const scenarioLabel = (m) => {
  const label = MESSAGE_SCENARIOS.find((s) => s.value === m.scenario)?.label || m.scenario;
  const statuses = m.criteria?.loan_statuses;
  return Array.isArray(statuses) && statuses.length ? `${label}: ${statuses.join(', ')}` : label;
};

const PAGE_SIZE = 20;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Save a file sent with a message (the download needs the login token).
const downloadAttachment = async (messageId, a) => {
  const res = await axios.get(apiUrl(`/api/client-messages/sent/${messageId}/attachments/${a.id}`), {
    headers: authHeaders(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = a.filename;
  link.click();
  URL.revokeObjectURL(url);
};

// One sent message: what it said, its files, who sent it, everyone it went
// to, and Resend (to everyone, or only those it failed for), which sends it
// again as a new message and opens that one (onResent).
const SentMessageDetails = ({ id, onClose, onResent, onOpen }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    axios.get(apiUrl(`/api/client-messages/sent/${id}`), { headers: authHeaders() })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'The message could not be loaded.'); });
    return () => { cancelled = true; };
  }, [id]);

  const resend = async (onlyFailed) => {
    const count = onlyFailed ? Number(data.failed_count) : data.recipients.length;
    if (!window.confirm(`Send "${data.subject}" again to ${count} recipient${count === 1 ? '' : 's'}${onlyFailed ? ' it failed for' : ''}?`)) return;
    setResending(true);
    setError('');
    try {
      const res = await axios.post(apiUrl(`/api/client-messages/sent/${id}/resend`), { only_failed: onlyFailed }, { headers: authHeaders() });
      onResent(res.data.message_id);
    } catch (err) {
      setError(err.response?.data?.error || 'The message could not be sent again.');
    } finally {
      setResending(false);
    }
  };

  const footer = data ? (
    <>
      {Number(data.failed_count) > 0 && (
        <Button variant="warning" onClick={() => resend(true)} disabled={resending} icon={<RotateCw size={15} />}>
          Resend to the {data.failed_count} that failed
        </Button>
      )}
      <Button variant="primary" onClick={() => resend(false)} disabled={resending || !data.recipients.length} icon={<RotateCw size={15} />}>
        {resending ? 'Sending…' : 'Resend to everyone'}
      </Button>
    </>
  ) : null;

  return (
    <FullScreenPanel
      isOpen
      onClose={onClose}
      title={data?.subject || 'Sent message'}
      subtitle={data ? `Sent ${formatDateTimeDMY(data.created_at)}${data.sent_by_email ? ` by ${data.sent_by_email}` : ''}` : null}
      footer={footer}
    >
      {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" role="alert">{error}</div>}
      {!data && !error && <p className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">Loading message…</p>}
      {data && (
        <div className="grid grid-cols-6 gap-4">
          <FieldGroup title="The message" tone="blue" wide>
            <DisplayField label="Sent on">{formatDateTimeDMY(data.created_at)}</DisplayField>
            <DisplayField label="Sent by">{data.sent_by_email}</DisplayField>
            <DisplayField label="Sent to">{scenarioLabel(data)}</DisplayField>
            <DisplayField label="Recipients">{data.recipient_count}</DisplayField>
            <DisplayField label="Sent" tone="good">{data.sent_count}</DisplayField>
            <DisplayField label="Failed" tone={Number(data.failed_count) > 0 ? 'alert' : null}>{data.failed_count}</DisplayField>
            {data.resent_from && (
              <DisplayField label="Resent from">
                <button type="button" onClick={() => onOpen(data.resent_from)} className={LINK_TEXT}>Message #{data.resent_from}</button>
              </DisplayField>
            )}
          </FieldGroup>
          <FieldGroup title={data.subject} tone="slate" wide bare>
            {data.message_html ? (
              // Cleaned by the API before it was stored (utils/messageHtml.js).
              <div className={RICH_TEXT_CLASS} dangerouslySetInnerHTML={{ __html: data.message_html }} />
            ) : (
              <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words">{data.message}</p>
            )}
            {data.attachments?.length > 0 && (
              <ul className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex flex-wrap gap-2">
                {data.attachments.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => downloadAttachment(id, a).catch(() => setError(`${a.filename} could not be downloaded.`))}
                      title="Download"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/60 dark:hover:bg-blue-900/50"
                    >
                      <Paperclip size={12} />
                      <span className="max-w-[16rem] truncate">{a.filename}</span>
                      <span className="text-blue-600/70 dark:text-blue-300/70">{formatBytes(a.size_bytes)}</span>
                      <Download size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </FieldGroup>
          <FieldGroup title={`Recipients (${data.recipients.length})`} tone="slate" wide bare>
            <ResponsiveTable
              data={data.recipients}
              compact
              columns={[
                {
                  field: 'client_name',
                  header: 'Client',
                  formatter: (v, r) => (r.client_id
                    ? <><ClientLink clientId={r.client_id} name={v || `Client #${r.client_id}`} />{r.client_code ? ` (${r.client_code})` : ''}</>
                    : (v || '—')),
                },
                { field: 'email', header: 'Email' },
                { field: 'status', header: 'Status', formatter: (v) => <StatusBadge status={v} size="small" /> },
                { field: 'error', header: 'Why it failed', formatter: (v) => v || '' },
              ]}
              emptyMessage="No recipients were recorded."
            />
          </FieldGroup>
        </div>
      )}
    </FullScreenPanel>
  );
};

// Messages > Sent: every message sent from Messages > Send, newest first,
// read from GET /api/client-messages/sent. A row opens the message.
const SentMessages = () => {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  // Search as the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (search) params.set('q', search);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const res = await axios.get(apiUrl(`/api/client-messages/sent?${params}`), { headers: authHeaders() });
      setRows(res.data?.messages || []);
      setTotal(Number(res.data?.total) || 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(err.response?.data?.error || 'The sent messages could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [page, search, from, to]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtering = Boolean(q || from || to);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[240px]">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject, message or sender…"
            aria-label="Search sent messages"
            className="toolbar-control flex-1 min-w-[200px]"
          />
          <label className="text-xs text-gray-500 dark:text-slate-400" htmlFor="sent-from">From</label>
          <input id="sent-from" type="date" value={from} max={to || undefined}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="toolbar-control w-40" />
          <label className="text-xs text-gray-500 dark:text-slate-400" htmlFor="sent-to">To</label>
          <input id="sent-to" type="date" value={to} min={from || undefined}
            onChange={(e) => { setTo(e.target.value); setPage(1); }} className="toolbar-control w-40" />
          {filtering && (
            <button type="button" onClick={() => { setQ(''); setFrom(''); setTo(''); setPage(1); }}
              className="toolbar-control px-2 text-sm text-blue-600 dark:text-blue-300 hover:underline">
              Clear filters
            </button>
          )}
        </div>
        <Pager page={page} pages={pages} onPage={setPage} total={total} disabled={loading} className="ml-auto" />
      </div>

      {error && <div className="mb-3 p-3 rounded-lg text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" role="alert">{error}</div>}

      <ResponsiveTable
        data={rows}
        isLoading={loading}
        onRowClick={(row) => setOpenId(row.id)}
        columns={[
          { field: 'created_at', header: 'Sent on', formatter: (v) => formatDateTimeDMY(v) },
          {
            field: 'subject',
            header: 'Message',
            formatter: (v, row) => (
              <div className="min-w-0 max-w-[28rem]">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-medium text-gray-800 dark:text-slate-100 truncate">{v}</span>
                  {Number(row.attachment_count) > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-gray-500 dark:text-slate-400 flex-shrink-0" title={`${row.attachment_count} attachment${row.attachment_count === 1 ? '' : 's'}`}>
                      <Paperclip size={12} />{row.attachment_count}
                    </span>
                  )}
                  {row.resent_from && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60 flex-shrink-0">Resent</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{row.message}</div>
              </div>
            ),
          },
          { field: 'scenario', header: 'Sent to', formatter: (v, row) => scenarioLabel(row) },
          { field: 'recipient_count', header: 'Recipients' },
          {
            field: 'sent_count',
            header: 'Sent / Failed',
            formatter: (v, row) => (
              <span>
                <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{v}</span>
                {' / '}
                <span className={Number(row.failed_count) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>{row.failed_count}</span>
              </span>
            ),
          },
          { field: 'sent_by_email', header: 'Sent by', formatter: (v) => v || '—' },
        ]}
        emptyMessage={filtering ? 'No sent messages match these filters.' : 'No messages have been sent yet.'}
      />

      {openId && (
        <SentMessageDetails
          id={openId}
          onClose={() => setOpenId(null)}
          onOpen={setOpenId}
          onResent={(newId) => { setOpenId(newId); load(); }}
        />
      )}
    </div>
  );
};

export default SentMessages;
